// Reine Filter-/Anzeigelogik für das Suchfeld mit Vorschlägen in
// UebergabeVorauswahl.tsx, das die frühere <select>-Liste ersetzt (rund 1060 Mieter,
// 218 Eigentümer im Bestand — die aufgeklappte Liste füllte auf dem Tablet den ganzen
// Bildschirm und war praktisch unbedienbar). Die eigentliche Suche läuft serverseitig
// über ilike, entprellt und erst ab MIN_SUCHLAENGE Zeichen (Muster: TicketFormular.tsx/
// TicketVerknuepfungen.tsx) — hier steht nur das, was sich ohne Netzwerk testen lässt:
// die ilike-Bedingung, der Schwellwert, das Ausblenden der jeweils anderen Auswahl
// (Käufer ↔ bisheriger Eigentümer beim „kauf") und die Anzeigenamen-Bildung.

/** Ab wie vielen Zeichen die serverseitige Suche losläuft (Muster: TicketFormular.tsx/TicketVerknuepfungen.tsx). */
export const MIN_SUCHLAENGE = 2;

/** Höchstzahl gleichzeitig angezeigter Vorschläge — bewusst überschaubar, kein Abbild der ganzen Kartei. */
export const MAX_VORSCHLAEGE = 8;

export interface PersonOption {
  id: string;
  name: string;
}

/**
 * PostgREST-`.or()`-Bedingung für die Mieter-Suche: Vor- und Nachname. `ilike` ist
 * bereits groß-/kleinschreibungsunabhängig, `query` sollte vorher getrimmt sein.
 */
export function mieterSuchBedingung(query: string): string {
  return `vorname.ilike.%${query}%,nachname.ilike.%${query}%`;
}

/**
 * Dieselbe Bedingung für Eigentümer, zusätzlich über den Firmennamen — Firma ist bei
 * Eigentümern ein vollwertiger Anzeigename (s. lib/eigentuemer/anzeige.ts), muss also
 * auch auffindbar sein.
 */
export function eigentuemerSuchBedingung(query: string): string {
  return `vorname.ilike.%${query}%,nachname.ilike.%${query}%,firma_name.ilike.%${query}%`;
}

/** Ob bei diesem Suchtext bereits serverseitig gesucht werden soll (statt der Vorschlagsliste ohne Eingabe zu zeigen). */
export function sollServerSuchen(query: string): boolean {
  return query.trim().length >= MIN_SUCHLAENGE;
}

/**
 * Blendet die jeweils andere getroffene Auswahl aus einer Trefferliste aus — Käufer und
 * bisheriger Eigentümer dürfen bei „kauf" nicht dieselbe Person sein (Fix-Runde 1,
 * Review). Gilt jetzt für Suchergebnisse statt, wie zuvor, für die volle Eigentümerliste.
 */
export function ohneAndereAuswahl(treffer: PersonOption[], andereId: string | null): PersonOption[] {
  if (!andereId) return treffer;
  return treffer.filter((p) => p.id !== andereId);
}

/** Begrenzt eine Liste auf höchstens `max` Einträge — Vorschläge bleiben überschaubar. */
export function begrenze<T>(liste: T[], max: number = MAX_VORSCHLAEGE): T[] {
  return liste.length > max ? liste.slice(0, max) : liste;
}

/** Anzeigename für Mieter — Firma (falls hinterlegt) sonst Vor-/Nachname. Fällt nie auf leer zurück. */
export function mieterAnzeigename(m: { vorname?: string | null; nachname?: string | null; firma_name?: string | null }): string {
  const firma = m.firma_name?.trim();
  if (firma) return firma;
  return `${m.vorname ?? ''} ${m.nachname ?? ''}`.replace(/\s+/g, ' ').trim() || '—';
}
